import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Sparkles, Plus, Trash2, Send, Loader2, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BrinxChat = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const bottomRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/brinx/sessions`, { headers: authHeaders() });
      setSessions(res.data);
    } catch (e) {
      console.error('BRINX sessions error:', e);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openSession = async (session) => {
    setActiveSession(session);
    try {
      const res = await axios.get(`${API_URL}/api/brinx/sessions/${session.id}/messages`, { headers: authHeaders() });
      setMessages(res.data);
    } catch (e) {
      setMessages([]);
    }
  };

  const newSession = async () => {
    const res = await axios.post(`${API_URL}/api/brinx/sessions`, {}, { headers: authHeaders() });
    setSessions(prev => [res.data, ...prev]);
    setActiveSession(res.data);
    setMessages([]);
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    await axios.delete(`${API_URL}/api/brinx/sessions/${sessionId}`, { headers: authHeaders() });
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    if (activeSession?.id === sessionId) {
      setActiveSession(null);
      setMessages([]);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    let session = activeSession;
    if (!session) {
      const res = await axios.post(`${API_URL}/api/brinx/sessions`, {}, { headers: authHeaders() });
      session = res.data;
      setSessions(prev => [session, ...prev]);
      setActiveSession(session);
    }

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    try {
      const response = await fetch(`${API_URL}/api/brinx/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: session.id, message: text }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();
        for (const evt of events) {
          if (!evt.startsWith('data: ')) continue;
          const data = evt.slice(6);
          if (data === '[DONE]') continue;
          if (data.startsWith('[ERROR]')) {
            setMessages(prev => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: 'assistant', content: `Eroare: ${data.replace('[ERROR]', '').trim()}` };
              return copy;
            });
            continue;
          }
          const chunk = data.replace(/\\n/g, '\n');
          setMessages(prev => {
            const copy = [...prev];
            copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
      fetchSessions();
    } catch (e) {
      console.error('BRINX chat error:', e);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-260px)] min-h-[500px]" data-testid="brinx-chat">
      {/* Sessions sidebar */}
      <Card className="w-64 flex flex-col p-3 shrink-0">
        <Button onClick={newSession} className="mb-3 w-full" data-testid="brinx-new-session-btn">
          <Plus className="h-4 w-4 mr-2" /> Conversație nouă
        </Button>
        <div className="flex-1 overflow-y-auto space-y-1">
          {loadingSessions && <p className="text-sm text-gray-400 text-center py-4">Se încarcă...</p>}
          {sessions.map(s => (
            <div
              key={s.id}
              onClick={() => openSession(s)}
              data-testid={`brinx-session-${s.id}`}
              className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer text-sm ${
                activeSession?.id === s.id ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
              }`}
            >
              <span className="truncate flex-1">{s.title || 'Conversație nouă'}</span>
              <Trash2
                className="h-3.5 w-3.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 shrink-0 ml-2"
                onClick={(e) => deleteSession(e, s.id)}
                data-testid={`brinx-delete-session-${s.id}`}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Chat area */}
      <Card className="flex-1 flex flex-col p-4">
        <div className="flex items-center gap-2 pb-3 border-b mb-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-semibold">BRINX AI</p>
            <p className="text-xs text-gray-500">Asistent pentru dobânzi și produse bancare</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2" data-testid="brinx-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <MessageSquare className="h-12 w-12 mb-3" />
              <p className="text-sm">Întreabă-mă orice despre dobânzile și produsele băncilor.</p>
              <p className="text-xs mt-1">Ex: „Ce bancă are cea mai mică dobândă fixă 5 ani pentru clasa A cu virare salariu?"</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'
              }`}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : '')}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="flex gap-2 pt-3 border-t mt-3">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Scrie întrebarea ta..."
            disabled={streaming}
            data-testid="brinx-input"
          />
          <Button onClick={sendMessage} disabled={streaming || !input.trim()} data-testid="brinx-send-btn">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default BrinxChat;
